import { BriefDeliverableStatusEnum } from "@entities/brief/lib/constants";
import { useApproveTaskContent } from "@entities/brief/lib/hooks/useApproveTaskContent";
import { useEditTaskContent } from "@entities/campaigns/lib/hooks/useEditTaskContent";
import { useUploadTaskContent } from "@entities/campaigns/lib/hooks/useUploadTaskContent";
import { useGetMe } from "@entities/Onboarding/lib/hooks/useGetMe";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { ToastService } from "@shared/lib/services";
import {
  ICampaignDeliverableTask,
  ICampaignDeliverableTaskApprove,
} from "@shared/lib/types/briefCampains";
import { Button } from "@shared/ui";
import { DisabledPopover } from "@shared/ui/DisabledPopover";
import { User } from "@shared/ui/icons";
import { Edit2 } from "lucide-react";
import { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { BriefHistoryPopover } from "./ui/BriefHistoryPopover";
import { BriefPreviewCard } from "./ui/BriefPreviewCard";
import { CopyBriefModal } from "./ui/CopyBriefModal";
import { CreatorBriefEditorDrawer } from "./ui/CreatorBriefEditorDrawer";
import { EmptyBriefState } from "./ui/EmptyBriefState";

interface Props {
  taskId: string;
  campaignInfluencerId: string;
  deliverableConfigRefId: string;
  task?: ICampaignDeliverableTask;
  refetchInfluencer: () => void;
  briefId: string;
  isLastApproval?: boolean;
}

export default function AddCampaignBrief({
  task,
  campaignInfluencerId,
  deliverableConfigRefId,
  taskId,
  refetchInfluencer,
  briefId,
  isLastApproval,
}: Props) {
  const { uploadTaskContent, isUploading, isUploaded } = useUploadTaskContent(
    briefId,
    refetchInfluencer
  );
  const { brief } = useBriefStore();
  const { userDetails } = useGetMe();
  const { editTaskContent, isEditing: isSavingEdit, isEdited } =
    useEditTaskContent(briefId, refetchInfluencer);
  const { approveTaskContent, isApproving, isApproved } =
    useApproveTaskContent(briefId);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const taskForm = task?.form;
  const briefHtml = taskForm?.title || "";
  const isAddBriefDisabled = false;
  const isTaskUpdated =
    new Date(taskForm?.updatedAt || "").getTime() >
    new Date(taskForm?.createdAt || "").getTime();

  useEffect(() => {
    if (isUploaded || isEdited) {
      setIsDrawerOpen(false);
      ToastService.success(
        isUploaded ? "Brief saved successfully" : "Brief edited successfully"
      );
    }
  }, [isUploaded, isEdited]);

  useEffect(() => {
    if (isApproved) {
      ToastService.success(
        isLastApproval ? "Approved" : "Forwarded with instructions"
      );
    }
  }, [isApproved, isLastApproval]);

  const persistBrief = (html: string) => {
    const payload = {
      campaignInfluencerId,
      deliverableConfigRefId,
      taskId,
      contentUrls: [],
      title: html.trim() || undefined,
    };
    if (taskForm) {
      editTaskContent({ data: payload });
    } else {
      uploadTaskContent({ data: payload });
    }
  };

  const handleApprove = () => {
    const payload: ICampaignDeliverableTaskApprove = {
      campaignInfluencerId,
      deliverableConfigRefId,
      taskId,
      remarks: "",
    };
    approveTaskContent({ data: payload });
  };

  const copyModal = (
    <CopyBriefModal
      open={isCopyModalOpen}
      briefId={briefId}
      deliverableConfigRefId={deliverableConfigRefId}
      taskId={taskId}
      campaignInfluencerId={campaignInfluencerId}
      onClose={() => setIsCopyModalOpen(false)}
      refetchInfluencer={refetchInfluencer}
    />
  );

  const editorDrawer = (
    <CreatorBriefEditorDrawer
      open={isDrawerOpen}
      initialHtml={briefHtml}
      isSaving={isUploading || isSavingEdit}
      onClose={() => setIsDrawerOpen(false)}
      onSave={persistBrief}
    />
  );

  if (!taskForm) {
    return (
      <>
        <EmptyBriefState
          isDisabled={isAddBriefDisabled}
          onCopy={() => setIsCopyModalOpen(true)}
          onAdd={() => setIsDrawerOpen(true)}
        />
        {editorDrawer}
        {copyModal}
      </>
    );
  }

  return (
    <>
      <div className={styles.briefContent}>
        <BriefPreviewCard
          html={briefHtml}
          onOpen={() => setIsDrawerOpen(true)}
        />
        <div className={styles.briefFooter}>
          <div className={styles.briefMeta}>
            <div className={styles.briefMetaIcon}>
              <User size={10} />
            </div>
            <span>
              {isTaskUpdated ? "Updated" : "Created"} on{" "}
              {new Date(
                isTaskUpdated ? taskForm.updatedAt : taskForm.createdAt
              ).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          </div>
          {taskForm.history && taskForm.history.length > 0 && (
            <BriefHistoryPopover history={taskForm.history} />
          )}
          <div className={styles.briefActions}>
            <DisabledPopover isDisabled={isAddBriefDisabled}>
              <Button
                disabled={isAddBriefDisabled || isApproving}
                onClick={() => setIsDrawerOpen(true)}
                className={styles.editButton}
              >
                <Edit2 size={13} /> Edit
              </Button>
            </DisabledPopover>
            {task?.status === BriefDeliverableStatusEnum.PendingApproval && (
              <Button
                type="primary"
                loading={isApproving}
                onClick={handleApprove}
                disabled={
                  !brief?.canInternalTeamTakeOpsActionsOnBehalfOfBrand &&
                  !userDetails?.userEntity?.permissions?.includes("super")
                }
              >
                Approve
              </Button>
            )}
          </div>
        </div>
      </div>
      {editorDrawer}
      {copyModal}
    </>
  );
}
